import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  TrendingUp, TrendingDown, LogOut, RefreshCw, PieChart, 
  Wallet, BarChart3, ArrowUpRight, ArrowDownRight, Building2,
  ChevronRight, Loader2, AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InvestorDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [investor, setInvestor] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const token = localStorage.getItem("investor_token");

  const fetchDashboard = useCallback(async () => {
    if (!token) {
      navigate("/investor/login");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/investor-portal/dashboard`, {
        headers: { "X-Investor-Token": token }
      });
      setDashboardData(response.data);
      setInvestor(response.data.investor);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("investor_token");
        localStorage.removeItem("investor_data");
        toast.error("Session expired. Please login again.");
        navigate("/investor/login");
      } else {
        toast.error("Failed to load portfolio data");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/investor-auth/logout/${token}`);
    } catch (e) {
      // Log but don't block logout - user should still be logged out locally
      console.warn("Logout API call failed:", e.message);
    }
    localStorage.removeItem("investor_token");
    localStorage.removeItem("investor_data");
    toast.success("Logged out successfully");
    navigate("/investor/login");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value) => {
    const formatted = Math.abs(value).toFixed(2);
    return value >= 0 ? `+${formatted}%` : `-${formatted}%`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading your portfolio...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <Card className="bg-slate-900/80 border-slate-800 max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No Portfolio Data</h2>
            <p className="text-slate-400 mb-4">
              Your portfolio hasn't been imported yet. Please contact your distributor to import your CAS statement.
            </p>
            <Button onClick={handleLogout} variant="outline" className="border-slate-700 text-slate-300">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { summary, amc_breakdown, category_breakdown, top_holdings } = dashboardData;
  const isProfit = summary.total_gain >= 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="bg-slate-900/80 border-b border-slate-800 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-slate-900" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Paisa Secure</h1>
                <p className="text-xs text-slate-400">Investor Portal</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-white">{investor?.name || "Investor"}</p>
                <p className="text-xs text-slate-400">PAN: {investor?.pan?.slice(0, 4)}****</p>
              </div>
              <Button
                data-testid="refresh-btn"
                onClick={fetchDashboard}
                variant="ghost"
                size="icon"
                className="text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="ghost"
                size="icon"
                className="text-slate-400 hover:text-red-400 hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="investor-dashboard">
        {/* Portfolio Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">Total Value</span>
                <Wallet className="w-5 h-5 text-amber-500" />
              </div>
              <p className="text-2xl font-bold text-white" data-testid="total-value">
                {formatCurrency(summary.total_value)}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Invested: {formatCurrency(summary.total_cost)}
              </p>
            </CardContent>
          </Card>

          <Card className={`bg-gradient-to-br ${isProfit ? 'from-emerald-500/10 to-emerald-600/5 border-emerald-500/20' : 'from-red-500/10 to-red-600/5 border-red-500/20'}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">Total Returns</span>
                {isProfit ? (
                  <ArrowUpRight className="w-5 h-5 text-emerald-500" />
                ) : (
                  <ArrowDownRight className="w-5 h-5 text-red-500" />
                )}
              </div>
              <p className={`text-2xl font-bold ${isProfit ? 'text-emerald-400' : 'text-red-400'}`} data-testid="total-returns">
                {formatCurrency(Math.abs(summary.total_gain))}
              </p>
              <p className={`text-sm mt-1 ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
                {formatPercentage(summary.gain_percentage)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/80 border-slate-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">Total Schemes</span>
                <PieChart className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-2xl font-bold text-white" data-testid="total-schemes">
                {summary.total_schemes}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Across {summary.total_amcs} AMCs
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/80 border-slate-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">Last Updated</span>
                <BarChart3 className="w-5 h-5 text-purple-400" />
              </div>
              <p className="text-lg font-semibold text-white">
                {dashboardData.last_updated 
                  ? new Date(dashboardData.last_updated).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                  : 'N/A'
                }
              </p>
              <p className="text-xs text-slate-400 mt-1">CAS Import Date</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: "overview", label: "Overview" },
            { id: "holdings", label: "Holdings" },
            { id: "amcs", label: "AMC Breakdown" }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? "bg-amber-500 text-slate-900"
                  : "bg-slate-800 text-slate-300 hover:bg-slate-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Category Breakdown */}
            <Card className="bg-slate-900/80 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-amber-500" />
                  Asset Allocation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {category_breakdown.map((cat) => (
                    <div key={cat.category} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-slate-300">{cat.category}</span>
                          <span className="text-sm font-medium text-white">{cat.allocation}%</span>
                        </div>
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all duration-500"
                            style={{ width: `${cat.allocation}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm text-slate-400 w-24 text-right">
                        {formatCurrency(cat.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Holdings */}
            <Card className="bg-slate-900/80 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                  Top Holdings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {top_holdings.map((holding) => (
                    <div key={holding.scheme_name || holding.folio_number} className="p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-start justify-between mb-1">
                        <h4 className="text-sm font-medium text-white line-clamp-1 flex-1 mr-2">
                          {holding.scheme_name}
                        </h4>
                        <span className={`text-sm font-medium ${holding.gain >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatPercentage(holding.gain_percentage)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <span>{holding.amc}</span>
                        <span>{formatCurrency(holding.current_value)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "holdings" && (
          <Card className="bg-slate-900/80 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white">All Holdings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-800">
                      <th className="text-left py-3 px-2 text-xs font-medium text-slate-400 uppercase">Scheme</th>
                      <th className="text-right py-3 px-2 text-xs font-medium text-slate-400 uppercase">Units</th>
                      <th className="text-right py-3 px-2 text-xs font-medium text-slate-400 uppercase">NAV</th>
                      <th className="text-right py-3 px-2 text-xs font-medium text-slate-400 uppercase">Value</th>
                      <th className="text-right py-3 px-2 text-xs font-medium text-slate-400 uppercase">Returns</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top_holdings.map((holding) => (
                      <tr key={holding.scheme_name || holding.folio_number} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                        <td className="py-3 px-2">
                          <p className="text-sm text-white line-clamp-1">{holding.scheme_name}</p>
                          <p className="text-xs text-slate-500">{holding.amc}</p>
                        </td>
                        <td className="py-3 px-2 text-right text-sm text-slate-300">
                          {holding.units?.toFixed(3)}
                        </td>
                        <td className="py-3 px-2 text-right text-sm text-slate-300">
                          {holding.nav?.toFixed(2)}
                        </td>
                        <td className="py-3 px-2 text-right text-sm font-medium text-white">
                          {formatCurrency(holding.current_value)}
                        </td>
                        <td className={`py-3 px-2 text-right text-sm font-medium ${holding.gain >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatPercentage(holding.gain_percentage)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === "amcs" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {amc_breakdown.map((amc) => (
              <Card key={amc.amc} className="bg-slate-900/80 border-slate-800 hover:border-amber-500/30 transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="font-medium text-white text-sm line-clamp-1">{amc.amc}</h3>
                        <p className="text-xs text-slate-400">{amc.schemes} scheme{amc.schemes > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <span className="text-xs text-slate-500 bg-slate-800 px-2 py-1 rounded">
                      {amc.allocation}%
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Value</span>
                      <span className="text-white font-medium">{formatCurrency(amc.value)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Invested</span>
                      <span className="text-slate-300">{formatCurrency(amc.cost)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Returns</span>
                      <span className={amc.gain >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                        {formatCurrency(amc.gain)} ({formatPercentage(amc.gain_pct)})
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Footer info */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>Portfolio data is based on your last imported CAS statement.</p>
          <p>Contact your distributor to update your portfolio data.</p>
        </div>
      </main>
    </div>
  );
};

export default InvestorDashboardPage;
